import { BaseComponent, define, prefersReducedMotion } from 'core';

export class CarouselSlider extends BaseComponent {
  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute('role')) this.setAttribute('role', 'region');
    this.setAttribute('aria-roledescription', 'carousel');

    this._track = this.querySelector('[data-carousel-track]');
    this._slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this._slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `${index + 1} / ${this._slides.length}`);
    });

    this._prevBtn = this.querySelector('[data-carousel-prev]');
    this._nextBtn = this.querySelector('[data-carousel-next]');
    if (this._prevBtn)
      this.on(this._prevBtn, 'click', () => {
        this.scrollBySlide(-1);
        this._userNavigated();
      });
    if (this._nextBtn)
      this.on(this._nextBtn, 'click', () => {
        this.scrollBySlide(1);
        this._userNavigated();
      });

    this.on(this, 'keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.scrollBySlide(-1);
        this._userNavigated();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.scrollBySlide(1);
        this._userNavigated();
      }
    });

    this._initLazy();
    this._initMore();

    this._dots = this.querySelector('[data-carousel-dots]');
    this._paginationLabel = this.getAttribute('data-pagination-label') || 'Go to page';

    if (this._track) {
      this.on(
        this._track,
        'scroll',
        () => {
          this._updateControls();
          this._updatePagination();
        },
        { passive: true }
      );
      this._updateControls();

      if (this._dots) {
        this._buildDots();

        // Kapalı bir <details> içinde (PDP "Complete your project" akordiyonu)
        // track genişliği bağlanma anında 0 olur ve sayfa sayısı yanlış çıkar.
        // Açılma bir window resize'ı DEĞİL, bu yüzden elemanı doğrudan izliyoruz;
        // ResizeObserver hem açılmayı hem kap genişliği değişimini yakalar.
        if ('ResizeObserver' in window) {
          this._resizeObserver = new ResizeObserver(() => {
            clearTimeout(this._resizeTimer);
            this._resizeTimer = setTimeout(() => this._buildDots(), 150);
          });
          this._resizeObserver.observe(this._track);
        } else {
          this.on(window, 'resize', () => {
            clearTimeout(this._resizeTimer);
            this._resizeTimer = setTimeout(() => this._buildDots(), 150);
          });
        }
      }
    }

    this._initAutoplay();
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    if (this._resizeObserver) this._resizeObserver.disconnect();
    this._stopAutoplay();
    super.disconnectedCallback();
  }

  /**
   * Otomatik geçiş — OPT-IN. Yalnızca `data-autoplay="<ms>"` verilirse çalışır,
   * yani mevcut carousel'lerin davranışı hiç değişmez (hero slider kullanıyor).
   *
   * Durur: prefers-reduced-motion açıksa hiç başlamaz; hover / klavye focus /
   * sekme arkaplana alındığında duraklar, geri dönünce devam eder. Sona
   * gelince başa sarar.
   */
  _initAutoplay() {
    const ms = parseInt(this.dataset.autoplay || '', 10);
    if (!ms || ms < 1000) return;
    if (prefersReducedMotion()) return;
    if (this._slides.length < 2) return;

    this._autoplayMs = ms;

    // Geri sayım halkası. Markup'ta duruyor ama GİZLİ: buraya kadar gelmek
    // otomatik geçişin gerçekten çalışacağı anlamına geliyor (reduced-motion,
    // tek slayt ve geçersiz süre yukarıda eleniyor). Böylece halka hiçbir zaman
    // dönmeyen bir sayaç göstermiyor.
    this._progress = this.querySelector('[data-carousel-progress]');
    if (this._progress) this._progress.hidden = false;

    // Halka ile sayaç AYNI ANDA başlamalı. CSS animasyonu öğe boyanır boyanmaz
    // dönmeye başlıyor, setInterval ise bileşen yükselene kadar (module + defer)
    // başlamıyor — arada yüzlerce ms fark oluyordu ve halka slayttan önce
    // doluyordu. CSS'te animasyon `paused` başlıyor; burada sıfırlanıp aynı
    // satırda çalıştırılıyor, iki saat tek noktadan kuruluyor.
    this._progressRestart();
    this._startAutoplay();

    this.on(this, 'mouseenter', () => this._stopAutoplay());
    this.on(this, 'mouseleave', () => this._startAutoplay());
    this.on(this, 'focusin', () => this._stopAutoplay());
    this.on(this, 'focusout', () => this._startAutoplay());
    this.on(document, 'visibilitychange', () => {
      if (document.hidden) this._stopAutoplay();
      else this._startAutoplay();
    });
  }

  /**
   * Halka duraklarken de doğruyu söylemeli: fareyi üstüne getirince sayaç
   * duruyorsa halka da durmalı, yoksa "birazdan geçecek" der ve geçmez.
   * `animation-play-state` bunu tek satırda veriyor; ilerlemeyi JS'te takip
   * etmeye gerek yok.
   */
  /**
   * Durum HOST'ta duruyor, halkanın kendisinde değil: mobilde geri sayımı
   * halka değil sayfa çubuğunun aktif dilimi gösteriyor ve o dilim CSS'te bir
   * ::after — JS'ten ona tutunmak gerekmiyor. Tek nitelik iki göstergeyi de
   * besliyor.
   */
  _progressResume() {
    this.setAttribute('data-running', '');
  }

  _progressPause() {
    this.removeAttribute('data-running');
  }

  /**
   * Kullanıcı elle geçtiğinde sayaç da baştan saymalı: yoksa oka basıp yarım
   * saniye sonra bir kez daha otomatik geçiyor ve halka o geçişi hiç
   * anlatmamış oluyor. Otomatik geçiş kapalıysa hiçbir şey yapmaz.
   */
  _userNavigated() {
    if (!this._autoplayTimer) return;
    this._stopAutoplay();
    this._progressRestart();
    this._startAutoplay();
  }

  /**
   * Sayaç sıfırlanınca halka da baştan başlamalı. CSS animasyonunu yeniden
   * tetiklemenin tek yolu onu kaldırıp geri koymak; arada bir reflow okunmazsa
   * tarayıcı iki değişikliği birleştirip hiçbir şey olmamış gibi davranıyor.
   */
  _progressRestart() {
    if (!this._progress) return;
    this._progress.style.animation = 'none';
    void this._progress.offsetWidth;
    this._progress.style.animation = '';
  }

  _startAutoplay() {
    if (!this._autoplayMs || this._autoplayTimer) return;
    this._autoplayTimer = setInterval(() => this._advance(), this._autoplayMs);
    this._progressResume();
  }

  _stopAutoplay() {
    if (!this._autoplayTimer) return;
    clearInterval(this._autoplayTimer);
    this._autoplayTimer = null;
    this._progressPause();
  }

  _advance() {
    if (!this._track) return;
    this._progressRestart();
    const maxScroll = this._track.scrollWidth - this._track.clientWidth;
    // 2px tolerans: subpixel yuvarlama yüzünden son slide'da eşitlik tutmayabiliyor.
    if (this._track.scrollLeft >= maxScroll - 2) {
      this._track.scrollTo({ left: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    } else {
      this.scrollBySlide(1);
    }
  }

  /**
   * Opsiyonel "show all" kırpması (CSS ile yapılır, bkz. collection.liquid).
   * JS'in tek işi: hiçbir şey kırpılmıyorsa (tüm kutucuklar zaten görünüyorsa)
   * butonu gizlemek. JS yoksa buton görünür kalır ve yine çalışır.
   */
  _initMore() {
    this._more = this.querySelector('[data-carousel-more]');
    this._moreToggle = this.querySelector('[data-carousel-more-toggle]');
    if (!this._more || !this._track) return;

    const update = () => {
      const expanded = this._moreToggle ? this._moreToggle.checked : false;
      const clipped = expanded || this._track.scrollHeight > this._track.clientHeight + 1;
      this._more.style.display = clipped ? '' : 'none';
    };

    update();
    if (this._moreToggle) this.on(this._moreToggle, 'change', update);
    this.on(window, 'resize', () => {
      clearTimeout(this._moreTimer);
      this._moreTimer = setTimeout(update, 150);
    });
  }

  _initLazy() {
    const lazyImages = Array.from(this.querySelectorAll('img[data-src]'));
    if (lazyImages.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      lazyImages.forEach((img) => this._loadImage(img));
      return;
    }

    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._loadImage(entry.target);
            this._observer.unobserve(entry.target);
          }
        });
      },
      { root: this._track, rootMargin: '200px' }
    );
    lazyImages.forEach((img) => this._observer.observe(img));
  }

  _loadImage(img) {
    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
      delete img.dataset.srcset;
    }
    if (img.dataset.src) {
      img.src = img.dataset.src;
      delete img.dataset.src;
    }
  }

  scrollBySlide(direction) {
    if (!this._track || this._slides.length === 0) return;
    const step = this._slides[0].getBoundingClientRect().width + this._gap();
    this._track.scrollBy({
      left: direction * step,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  _gap() {
    if (!this._track) return 0;
    const style = getComputedStyle(this._track);
    return parseFloat(style.columnGap || style.gap || '0') || 0;
  }

  _updateControls() {
    if (!this._track) return;
    const maxScroll = this._track.scrollWidth - this._track.clientWidth - 1;

    const hasOverflow = this._track.scrollWidth > this._track.clientWidth + 1;
    if (this._prevBtn) {
      this._prevBtn.style.display = hasOverflow ? '' : 'none';
      this._prevBtn.disabled = this._track.scrollLeft <= 0;
    }
    if (this._nextBtn) {
      this._nextBtn.style.display = hasOverflow ? '' : 'none';
      this._nextBtn.disabled = this._track.scrollLeft >= maxScroll;
    }
  }

  _pageCount() {
    const pageWidth = this._track.clientWidth;
    if (pageWidth === 0) return 1;
    return Math.max(1, Math.ceil((this._track.scrollWidth - 1) / pageWidth));
  }

  _buildDots() {
    if (!this._track || !this._dots) return;
    const pages = this._pageCount();

    if (this._dots.children.length !== pages) {
      this._dots.textContent = '';
      for (let i = 0; i < pages; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        // Tek parça ilerleme çubuğu: her sayfa flex-1 bir dilim (dokunma alanı
        // 24px yüksek, WCAG 2.5.8). Renk/yükseklik/boşluk src/input.css'te —
        // burada utility basmıyoruz ki tema o kuralları ezmek zorunda kalmasın.
        dot.className = 'group flex min-h-6 flex-1 cursor-pointer items-center';
        dot.setAttribute('aria-label', `${this._paginationLabel} ${i + 1}`);
        const dotVisual = document.createElement('span');
        dotVisual.className = 'w-full transition-colors';
        dot.appendChild(dotVisual);
        dot.addEventListener('click', () => {

          const left = i === pages - 1 ? this._track.scrollWidth : i * this._track.clientWidth;
          this._track.scrollTo({
            left,
            behavior: prefersReducedMotion() ? 'auto' : 'smooth',
          });
        });
        this._dots.appendChild(dot);
      }
    }

    // Tek sayfada çubuk yok. Masaüstünde ise HİÇ gösterilmiyor — bunu CSS
    // hallediyor (src/input.css, md ve üstü). Burada inline stil yalnızca
    // "sayfa sayısı yetersiz" durumunu kapatıyor; boş bırakıldığında medya
    // sorgusu devreye giriyor, yani iki kural birbirini ezmeden birleşiyor.
    this._dots.style.display = pages <= 1 ? 'none' : '';
    this._updatePagination();
  }

  _updatePagination() {
    if (!this._track || !this._dots || this._dots.children.length === 0) return;
    const pageWidth = this._track.clientWidth || 1;
    const lastIndex = this._dots.children.length - 1;
    const maxScroll = this._track.scrollWidth - this._track.clientWidth;

    let active;
    if (maxScroll > 0 && this._track.scrollLeft >= maxScroll - 1) {
      active = lastIndex;
    } else {
      active = Math.round(this._track.scrollLeft / pageWidth);
    }
    active = Math.max(0, Math.min(active, lastIndex));
    // Dolgu KÜMÜLATİF: 3 sayfadan 2.'deysek 1 ve 2 dolu. Çubuk "kaçıncı
    // sayfadayım" değil "listenin neresindeyim" sorusunu cevaplıyor.
    // aria-current yalnızca gerçek aktif dilimde kalıyor (ekran okuyucu için
    // tek bir "current" olmalı); dolgu ayrı bir data niteliğiyle işaretleniyor.
    Array.from(this._dots.children).forEach((dot, index) => {
      if (index === active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
      dot.toggleAttribute('data-filled', index <= active);
    });
  }
}

define('carousel-slider', CarouselSlider);
